import { gql } from '@apollo/client';
import { apolloClient } from '../graphql/client';
import { Item, ItemInput } from '../types/item';

class ItemService {
  // Get all items (including inactive)
  async getItems(): Promise<Item[]> {
    const query = gql`
      query GetAllItems {
        allItems {
          id
          code
          name
          description
          categoryCode
          subCategoryCode
          status
          position
          associatedCategoryCodes
          audioText
          imageData
          imageFileName
          imageGenerated
          createdDate
          updatedDate
          soundOptions {
            voiceId
            soundData
            soundFileName
            soundContentType
            soundGenerated
            soundGeneratedAt
          }
        }
      }
    `;

    try {
      console.log('Fetching items from GraphQL API...');
      const result = await apolloClient.query({
        query,
        fetchPolicy: 'network-only'
      });
      
      console.log('GraphQL result:', result);
      console.log('GraphQL result.data:', result.data);
      
      if (!result.data) {
        throw new Error('No data returned from GraphQL query');
      }
      
      if (!result.data.allItems) {
        console.error('AllItems property not found in response. Available properties:', Object.keys(result.data));
        throw new Error('AllItems property not found in GraphQL response');
      }
      
      console.log('Items fetched successfully:', result.data.allItems);
      return result.data.allItems;
    } catch (error: any) {
      console.error('Error fetching items:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch items: ${error.message}`);
      }
    }
  }

  // Get items by category
  async getItemsByCategory(categoryCode: string): Promise<Item[]> {
    const query = gql`
      query GetItemsByCategory($categoryCode: String!) {
        itemsByCategory(categoryCode: $categoryCode) {
          id
          code
          name
          description
          categoryCode
          subCategoryCode
          status
          position
          associatedCategoryCodes
          audioText
          imageData
          imageFileName
          imageGenerated
          createdDate
          updatedDate
          soundOptions {
            voiceId
            soundData
            soundFileName
            soundContentType
            soundGenerated
            soundGeneratedAt
          }
        }
      }
    `;

    try {
      const result = await apolloClient.query({
        query,
        variables: { categoryCode },
        fetchPolicy: 'network-only'
      });
      return result.data.itemsByCategory;
    } catch (error: any) {
      console.error('Error fetching items by category:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch items by category: ${error.message}`);
      }
    }
  }

  // Get item by code
  async getItemByCode(code: string): Promise<Item | null> {
    const query = gql`
      query GetItemByCode($code: String!) {
        itemByCode(code: $code) {
          id
          code
          name
          description
          categoryCode
          subCategoryCode
          status
          position
          associatedCategoryCodes
          audioText
          imageData
          imageFileName
          imageGenerated
          createdDate
          updatedDate
          soundOptions {
            voiceId
            soundData
            soundFileName
            soundContentType
            soundGenerated
            soundGeneratedAt
          }
        }
      }
    `;

    try {
      const result = await apolloClient.query({
        query,
        variables: { code },
        fetchPolicy: 'network-only'
      });
      return result.data.itemByCode;
    } catch (error: any) {
      console.error('Error fetching item by code:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to fetch item: ${error.message}`);
      }
    }
  }

  // Create new item
  async createItem(itemData: ItemInput): Promise<Item> {
    const mutation = gql`
      mutation CreateItem($item: ItemInput!) {
        createItem(item: $item) {
          id
          code
          name
          description
          categoryCode
          subCategoryCode
          status
          position
          associatedCategoryCodes
          audioText
          imageData
          imageFileName
          imageGenerated
          createdDate
          updatedDate
          soundOptions {
            voiceId
            soundData
            soundFileName
            soundContentType
            soundGenerated
            soundGeneratedAt
          }
        }
      }
    `;

    try {
      console.log('Creating item:', itemData);
      const result = await apolloClient.mutate({
        mutation,
        variables: { item: itemData }
      });
      console.log('Item created successfully:', result.data.createItem);
      return result.data.createItem;
    } catch (error: any) {
      console.error('Error creating item:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to create item: ${error.message}`);
      }
    }
  }

  // Update existing item
  async updateItem(id: string, itemData: ItemInput): Promise<Item> {
    const mutation = gql`
      mutation UpdateItem($id: ID!, $item: ItemInput!) {
        updateItem(id: $id, item: $item) {
          id
          code
          name
          description
          categoryCode
          subCategoryCode
          status
          position
          associatedCategoryCodes
          audioText
          imageData
          imageFileName
          imageGenerated
          createdDate
          updatedDate
          soundOptions {
            voiceId
            soundData
            soundFileName
            soundContentType
            soundGenerated
            soundGeneratedAt
          }
        }
      }
    `;

    try {
      // Clean soundOptions to remove __typename which causes GraphQL input errors
      const cleanedItemData = { ...itemData };
      if (cleanedItemData.soundOptions) {
        cleanedItemData.soundOptions = cleanedItemData.soundOptions.map((option: any) => {
          const { __typename, ...cleanOption } = option;
          return cleanOption;
        });
      }

      console.log('Updating item:', id, cleanedItemData);
      const result = await apolloClient.mutate({
        mutation,
        variables: { id, item: cleanedItemData }
      });
      console.log('Item updated successfully:', result.data.updateItem);
      return result.data.updateItem;
    } catch (error: any) {
      console.error('Error updating item:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to update item: ${error.message}`);
      }
    }
  }

  // Delete item
  async deleteItem(id: string): Promise<boolean> {
    const mutation = gql`
      mutation DeleteItem($id: ID!) {
        deleteItem(id: $id)
      }
    `;

    try {
      console.log('Deleting item:', id);
      const result = await apolloClient.mutate({
        mutation,
        variables: { id }
      });
      console.log('Item deleted successfully');
      return result.data.deleteItem;
    } catch (error: any) {
      console.error('Error deleting item:', error);
      
      if (error.networkError) {
        throw new Error(`Failed to connect to GraphQL API: ${error.networkError.message}`);
      } else if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        throw new Error(`GraphQL error: ${error.graphQLErrors[0].message}`);
      } else {
        throw new Error(`Failed to delete item: ${error.message}`);
      }
    }
  }

  // Generate audio for item
  async generateAudio(text: string, voice: string, itemCode: string): Promise<{ success: boolean; message: string; audioUrl?: string }> {
    const mutation = gql`
      mutation GenerateItemAudio($text: String!, $voice: String!, $itemCode: String!) {
        generateItemAudio(text: $text, voice: $voice, itemCode: $itemCode) {
          success
          message
          audioUrl
        }
      }
    `;

    try {
      console.log('Generating audio for item:', itemCode);
      const result = await apolloClient.mutate({
        mutation,
        variables: { text, voice, itemCode }
      });
      return result.data.generateItemAudio;
    } catch (error: any) {
      console.error('Error generating audio:', error);
      throw new Error(`Failed to generate audio: ${error.message}`);
    }
  }

  // Generate image for item
  async generateImage(prompt: string, size: string, itemCode: string): Promise<{ success: boolean; message: string; imageUrl?: string }> {
    const mutation = gql`
      mutation GenerateItemImage($prompt: String!, $size: String!, $itemCode: String!) {
        generateItemImage(prompt: $prompt, size: $size, itemCode: $itemCode) {
          success
          message
          imageUrl
        }
      }
    `;

    try {
      console.log('Generating image for item:', itemCode);
      const result = await apolloClient.mutate({
        mutation,
        variables: { prompt, size, itemCode }
      });
      return result.data.generateItemImage;
    } catch (error: any) {
      console.error('Error generating image:', error);
      throw new Error(`Failed to generate image: ${error.message}`);
    }
  }
}

const itemService = new ItemService();
export default itemService;
